/**
 * Founder Control Center v2 · Operations · Marketplace overview.
 *
 * Vista admin del marketplace público: counters reales del sitemap + iframe del
 * marketplace para QA visual + drill-down al gestor (Directorio sub-tab dentro
 * de Centros).
 *
 * Fuentes (todas existentes · 0 backend tocado · 0 endpoints nuevos · 0 mocks):
 *   GET /api/seo/sitemap.xml        → counts centros + ciudades publicados
 *   GET /api/founder-os/home        → funnel_30d.marketplace_views (visibilidad)
 *   iframe /marketplace             → QA visual del público
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { Storefront, ArrowsClockwise, Eye, ArrowRight, Buildings, MapPin } from "@phosphor-icons/react";

function countByType(urls) {
  const c = { centros: 0, ciudades: 0, otros: 0 };
  urls.forEach((u) => {
    try {
      const p = new URL(u).pathname;
      if (p.startsWith("/centro/")) c.centros += 1;
      else if (p.startsWith("/ciudad/")) c.ciudades += 1;
      else c.otros += 1;
    } catch { c.otros += 1; }
  });
  return c;
}

export default function MarketplaceOverview() {
  const [sitemapUrls, setSitemapUrls] = useState([]);
  const [home, setHome] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const [s, h] = await Promise.allSettled([
        api.get("/seo/sitemap.xml", { responseType: "text", transformResponse: (v) => v }),
        api.get("/founder-os/home"),
      ]);
      if (s.status === "fulfilled" && typeof s.value.data === "string") {
        try {
          const doc = new DOMParser().parseFromString(s.value.data, "application/xml");
          setSitemapUrls(Array.from(doc.getElementsByTagName("loc")).map((n) => n.textContent || ""));
        } catch { setSitemapUrls([]); }
      }
      if (h.status === "fulfilled") setHome(h.value.data);
      else setErr(h.reason?.response?.data?.detail || "no disponible");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const counts = useMemo(() => countByType(sitemapUrls), [sitemapUrls]);
  const mpUrl = `${process.env.REACT_APP_BACKEND_URL}/marketplace`;

  return (
    <div className="space-y-4" data-testid="marketplace-overview-root">
      <header className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--ag-gold)] font-semibold flex items-center gap-1.5">
            <Storefront size={11} weight="fill" /> Operations · Marketplace
          </div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-[var(--ag-text)] mt-1">
            Marketplace público
          </h2>
          <p className="text-xs text-[var(--ag-text-dim)] mt-1">
            Estado de la plataforma de cara al consumidor. Counters reales del sitemap. Gestión de alta/visibilidad vive en <strong>Centros · Directorio</strong>.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-1.5 text-xs text-[var(--ag-text-dim)] hover:text-[var(--ag-gold)] px-3 py-2 rounded border border-[var(--ag-border)] hover:border-[var(--ag-gold)]/50 disabled:opacity-50"
          data-testid="marketplace-refresh"
        >
          <ArrowsClockwise size={13} className={loading ? "animate-spin" : ""} /> Refrescar
        </button>
      </header>

      {err && (
        <div className="text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded px-3 py-2" data-testid="marketplace-error">{err}</div>
      )}

      <section className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="marketplace-kpis">
        <KpiBox icon={Buildings} label="Centros publicados" value={counts.centros} sublabel="URLs /centro/* en sitemap" tone="info" testid="marketplace-kpi-centros" />
        <KpiBox icon={MapPin}    label="Ciudades indexadas" value={counts.ciudades} sublabel="URLs /ciudad/* en sitemap" tone="info" testid="marketplace-kpi-ciudades" />
        <KpiBox icon={Eye}       label="Impresiones · 30d" value={home?.funnel_30d?.marketplace_views ?? "—"} sublabel="Vistas públicas marketplace" testid="marketplace-kpi-views" />
        <KpiBox icon={ArrowRight} label="Mkt → Lead %"    value={home?.funnel_30d?.marketplace_to_lead_pct != null ? `${home.funnel_30d.marketplace_to_lead_pct}%` : "—"} sublabel="Conversión funnel" testid="marketplace-kpi-conv" />
      </section>

      <section className="rounded-lg border border-[var(--ag-border)] bg-[var(--ag-surface)] p-4" data-testid="marketplace-iframe-wrap">
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <Eye size={14} weight="duotone" className="text-[var(--ag-gold)]" />
            <h3 className="text-xs uppercase tracking-[0.22em] text-[var(--ag-text-dim)] font-semibold">
              Vista pública · QA visual
            </h3>
          </div>
          <a
            href={mpUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[10px] uppercase tracking-wider text-[var(--ag-gold)] hover:underline inline-flex items-center gap-1"
            data-testid="marketplace-link"
          >
            Abrir en nueva pestaña <ArrowRight size={10} />
          </a>
        </div>
        <div className="aspect-video w-full rounded border border-[var(--ag-border)] overflow-hidden bg-black">
          <iframe
            src={mpUrl}
            title="Marketplace público AutoGlow"
            className="w-full h-full"
            sandbox="allow-scripts allow-same-origin"
            data-testid="marketplace-iframe"
          />
        </div>
      </section>

      <section className="rounded-lg border border-[var(--ag-gold)]/25 bg-[var(--ag-gold)]/5 p-4" data-testid="marketplace-actions">
        <div className="text-[10px] uppercase tracking-wider text-[var(--ag-gold)] font-semibold mb-2">
          Gestión asociada
        </div>
        <p className="text-xs text-[var(--ag-text-dim)]">
          Alta, aprobación, descubrimiento OSM/RASIC, enriquecimiento y resolución de duplicados se realizan en Centros · Directorio.
        </p>
        <Link
          to="/founder/operations?sub=centros"
          className="inline-flex items-center gap-1.5 mt-3 text-xs px-3 py-2 rounded border border-[var(--ag-gold)]/40 bg-[var(--ag-gold)]/10 text-[var(--ag-gold)] hover:bg-[var(--ag-gold)]/20"
          data-testid="marketplace-to-directory"
        >
          <Buildings size={12} /> Ir a Centros · Directorio <ArrowRight size={11} />
        </Link>
      </section>
    </div>
  );
}

function KpiBox({ icon: Icon, label, value, sublabel, tone, testid }) {
  const cls = tone === "info"
    ? "border-[var(--ag-gold)]/40 bg-[var(--ag-gold)]/10 text-[var(--ag-gold)]"
    : "border-[var(--ag-border)] bg-[var(--ag-surface)] text-[var(--ag-text)]";
  return (
    <div className={`rounded-lg border p-3.5 ${cls}`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[9px] uppercase tracking-wider text-[var(--ag-text-muted)] font-semibold">{label}</div>
        {Icon && <Icon size={14} weight="duotone" />}
      </div>
      <div className="font-display text-2xl font-black tabular-nums mt-1 leading-none">{value}</div>
      <div className="text-[10px] text-[var(--ag-text-dim)] mt-1">{sublabel}</div>
    </div>
  );
}
